import apiClient from '../config'
import { extractApiErrorMessage } from './extract-api-error-message'
import { normalizeBotType } from './adapters/image/allall/midjourney'
import {
  pickFirstNonEmpty,
  extractCreateId,
  extractTaskId,
  extractData,
  extractPollingUrlFromPollResponse
} from './aim-unified-poll-common'
import { runWanUnifiedPollImageBranch } from './aim-unified-poll-image'
import { runWanUnifiedPollVideoBranch } from './aim-unified-poll-video'
import { runWanUnifiedPollMusicBranch } from './aim-unified-poll-music'

var POLL_INTERVAL = 15000
var POLL_TIMEOUT = 10 * 60 * 1000

function resolveWanVersion(modelId) {
  if (modelId === 'wan-2-2') return '2-2'
  if (modelId === 'wan-2-5') return '2-5'
  if (modelId === 'wan-2-6') return '2-6'
  return ''
}

function resolveWanAnimateVersion(params) {
  var mode = params && params.animateMode
  if (mode === 'replace') return '2-2-mix'
  return '2-2-move'
}

function buildPollodanceRefs(params) {
  var refs = []
  var order = 1
  var imgs = params && params.pollodanceRefImages
  if (Array.isArray(imgs)) {
    for (var i = 0; i < imgs.length; i++) {
      var u = imgs[i]
      if (u) refs.push({ type: 'image', name: 'ref-image-' + (i + 1), image: String(u), order: order++ })
    }
  }
  if (params && params.pollodanceRefVideo) {
    refs.push({ type: 'video', name: 'ref-video-1', video: String(params.pollodanceRefVideo), order: order++ })
  }
  if (params && params.pollodanceRefAudio) {
    refs.push({ type: 'audio', name: 'ref-audio-1', audio: String(params.pollodanceRefAudio), order: order++ })
  }
  return refs
}

function resolveWanProfile(params) {
  var modelId = params && params.modelId
  if (modelId === 'sora-2') {
    return {
      modelName: 'sora',
      modelVersion: '2-pro',
      modelType: 'video'
    }
  }
  if (modelId === 'seedance') {
    return {
      modelName: 'seedance',
      modelVersion: '1-5-pro',
      modelType: 'video'
    }
  }
  if (modelId === 'seegen') {
    var seegenVer = params && params.seegenGenMode === 'speed' ? 'speed' : 'stand'
    return {
      modelName: 'seegen',
      modelVersion: seegenVer,
      modelType: 'video'
    }
  }
  if (modelId === 'pollodance') {
    var pv = params && params.pollodanceVariant
    if (pv !== '2-0' && pv !== '2-0-fast' && pv !== '2-0-ref' && pv !== '2-0-fast-ref') pv = '2-0'
    return {
      modelName: 'pollodance',
      modelVersion: pv,
      modelType: 'video'
    }
  }
  if (modelId === 'runway') {
    return {
      modelName: 'runway',
      modelVersion: '4-5',
      modelType: 'video'
    }
  }
  if (modelId === 'hailuo-2-3') {
    return {
      modelName: 'hailuo',
      modelVersion: '2-3',
      modelType: 'video'
    }
  }
  if (modelId === 'vidu-q3-pro') {
    return {
      modelName: 'vidu',
      modelVersion: '3-pro',
      modelType: 'video'
    }
  }
  if (modelId === 'kling-2-5') {
    return {
      modelName: 'kling',
      modelVersion: '2-5',
      modelType: 'video'
    }
  }
  if (modelId === 'kling-2-6') {
    return {
      modelName: 'kling',
      modelVersion: '2-6',
      modelType: 'video'
    }
  }
  if (modelId === 'kling-3') {
    return {
      modelName: 'kling',
      modelVersion: '3',
      modelType: 'video'
    }
  }
  if (modelId === 'veo-3-0') {
    return {
      modelName: 'veo',
      modelVersion: '3-0',
      modelType: 'video'
    }
  }
  if (modelId === 'veo-3-1') {
    return {
      modelName: 'veo',
      modelVersion: '3-1',
      modelType: 'video'
    }
  }
  if (modelId === 'veo-3-1-fast') {
    return {
      modelName: 'veo',
      modelVersion: '3-1-fast',
      modelType: 'video'
    }
  }
  if (modelId === 'suno-music') {
    return {
      modelName: 'suno',
      modelVersion: '4-5',
      modelType: 'music'
    }
  }
  if (modelId === 'wan-animate') {
    return {
      modelName: 'wan-animate',
      modelVersion: resolveWanAnimateVersion(params),
      modelType: 'video'
    }
  }
  var version = resolveWanVersion(modelId)
  if (!version) return null
  return {
    modelName: 'wan',
    modelVersion: version,
    modelType: normalizeModelType(params && params.type)
  }
}

function normalizeModelType(v) {
  if (v === 'image' || v === 'music' || v === 'video') return v
  return 'video'
}

function normalizeResolution(v) {
  var s = String(v || '').trim()
  if (!s) return ''
  return s.replace(/p$/, 'P')
}

/** Vidu / Veo：分辨率后缀为小写 p（如 1080p） */
function normalizeResolutionLowercaseP(v) {
  var s = String(v || '').trim()
  if (!s) return ''
  return s.replace(/p$/i, 'p')
}

function buildBasePayload(modelName, modelVersion, modelType, action) {
  var miToken = typeof localStorage !== 'undefined' ? (localStorage.getItem('token') || '') : ''
  return {
    model_name: modelName,
    model_version: modelVersion,
    model_type: normalizeModelType(modelType),
    model_action: action,
    miToken: miToken
  }
}

export function submitWanUnifiedTask(params) {
  var profile = resolveWanProfile(params)
  if (!profile || !profile.modelVersion) return Promise.reject(new Error('Unsupported wan model: ' + (params && params.modelId)))
  var modelName = profile.modelName
  var modelVersion = profile.modelVersion
  var modelType = profile.modelType
  var body = buildBasePayload(modelName, modelVersion, modelType, 'create')
  if (modelName === 'sora') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.referenceImage) body.image = params.referenceImage
    if (params && params.videoRes) body.resolution = String(params.videoRes)
    if (params && params.videoDuration != null) body.length = params.videoDuration
    if (params && params.aspectRatio) body.aspectRatio = params.aspectRatio
  } else if (modelName === 'seedance') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.referenceImage) body.image = params.referenceImage
    if (params && params.endFrameImage) body.imageTail = params.endFrameImage
    if (params && params.aspectRatio) body.aspectRatio = params.aspectRatio
    if (params && params.videoDuration != null) body.length = params.videoDuration
    if (params && params.videoRes) body.resolution = String(params.videoRes)
    if (params && params.generateAudio != null) body.generateAudio = !!params.generateAudio
    if (params && params.seed != null && params.seed !== '') body.seed = params.seed
  } else if (modelName === 'seegen') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.videoMode === 'image' && params.referenceImage) {
      body.image = String(params.referenceImage)
    } else {
      body.image = ''
    }
    if (params && params.generateAudio != null) body.generateAudio = !!params.generateAudio
    if (params && params.aspectRatio) body.aspectRatio = String(params.aspectRatio)
    if (params && params.videoDuration != null) {
      body.length = params.videoDuration
    } else {
      body.length = 5
    }
    if (params && params.videoRes) body.resolution = String(params.videoRes)
    if (params && params.watermark != null) body.watermark = !!params.watermark
  } else if (modelName === 'pollodance') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    var pVer = modelVersion
    if (pVer === '2-0' || pVer === '2-0-fast') {
      if (params && params.videoMode === 'image' && params.referenceImage) {
        body.image = String(params.referenceImage)
      } else {
        body.image = ''
      }
    } else {
      body.image = ''
    }
    if (params && params.generateAudio != null) body.generateAudio = !!params.generateAudio
    if (params && params.aspectRatio) body.aspectRatio = String(params.aspectRatio)
    if (params && params.videoDuration != null) body.length = params.videoDuration
    else body.length = 10
    if (params && params.videoRes) body.resolution = String(params.videoRes)
    if ((pVer === '2-0-fast' || pVer === '2-0-fast-ref') && String(body.resolution || '').toLowerCase() === '1080p') {
      body.resolution = '720p'
    }
    if (params && params.webSearch != null) body.webSearch = !!params.webSearch
    var seedVal = params && params.seed
    body.seed = seedVal != null && seedVal !== '' ? Number(seedVal) : 0
    if (isNaN(body.seed)) body.seed = 0
    if (pVer === '2-0-ref' || pVer === '2-0-fast-ref') {
      var pref = buildPollodanceRefs(params)
      if (pref.length) body.refs = pref
    }
  } else if (modelName === 'runway') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    /* 测试用固定参考图，上线前改回 referenceImage */
    body.image = 'https://images.miralvis.com/uimages/20260417/dengwei_686/b8438548-418a-4146-aeab-f3a196bf446a.jpg'
    if (params && params.aspectRatio) body.aspectRatio = String(params.aspectRatio)
    if (params && params.videoDuration != null) {
      body.length = params.videoDuration
    } else {
      body.length = 5
    }
    var rwSeed = params && params.seed
    body.seed = rwSeed != null && rwSeed !== '' ? Number(rwSeed) : 0
    if (isNaN(body.seed)) body.seed = 0
  } else if (modelName === 'hailuo') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.videoMode === 'image' && params.referenceImage) {
      body.image = String(params.referenceImage)
    } else {
      body.image = ''
    }
    if (params && params.videoRes) {
      body.resolution = normalizeResolution(params.videoRes)
    } else {
      body.resolution = '768P'
    }
    if (params && params.videoDuration != null) {
      body.length = params.videoDuration
    } else {
      body.length = 6
    }
  } else if (modelName === 'vidu') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.videoMode === 'image' && params.referenceImage) {
      body.image = String(params.referenceImage)
    } else {
      body.image = ''
    }
    if (params && params.videoRes) {
      body.resolution = normalizeResolutionLowercaseP(params.videoRes)
    } else {
      body.resolution = '720p'
    }
    if (params && params.aspectRatio) {
      body.aspectRatio = String(params.aspectRatio)
    } else {
      body.aspectRatio = '16:9'
    }
    if (params && params.generateAudio != null) {
      body.generateAudio = !!params.generateAudio
    } else {
      body.generateAudio = true
    }
    if (params && params.videoDuration != null) {
      body.length = params.videoDuration
    } else {
      body.length = 5
    }
    var vuSeed = params && params.seed
    body.seed = vuSeed != null && vuSeed !== '' ? Number(vuSeed) : 0
    if (isNaN(body.seed)) body.seed = 0
  } else if (modelName === 'kling') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.videoMode === 'image') {
      if (params.referenceImage) body.image = String(params.referenceImage)
      if (params.endFrameImage) body.imageTail = String(params.endFrameImage)
    }
    if (params && params.videoMode === 'text' && params.aspectRatio) {
      body.aspectRatio = String(params.aspectRatio)
    }
    if (params && params.generateAudio != null) {
      body.generateAudio = !!params.generateAudio
    }
    if (params && params.videoDuration != null) {
      body.length = params.videoDuration
    }
    var klNp = params && params.negativePrompt ? String(params.negativePrompt).trim() : ''
    if (klNp) body.negativePrompt = klNp
  } else if (modelName === 'veo') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    body.negativePrompt = params && params.negativePrompt ? String(params.negativePrompt).trim() : ''
    if (params && params.videoDuration != null) {
      body.length = params.videoDuration
    } else {
      body.length = 5
    }
    if (params && params.videoRes) {
      body.resolution = normalizeResolutionLowercaseP(params.videoRes)
    }
    if (params && params.aspectRatio) {
      body.aspectRatio = String(params.aspectRatio)
    } else {
      body.aspectRatio = '16:9'
    }
    var veoSeed = params && params.seed
    if (veoSeed != null && veoSeed !== '' && !isNaN(Number(veoSeed)) && Number(veoSeed) >= 0) {
      body.seed = Number(veoSeed)
    } else {
      body.seed = 0
    }
    if (params && params.referenceImage) body.image = String(params.referenceImage)
    if (params && params.imageTail) body.imageTail = String(params.imageTail)
  } else if (modelName === 'suno') {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    var sunTitle = params && params.title ? String(params.title).trim() : ''
    if (sunTitle) body.title = sunTitle
    var styleStr = ''
    if (params && params.style) styleStr = String(params.style).trim()
    else if (params && params.tags) styleStr = String(params.tags).trim()
    else if (params && params.styleTags) styleStr = String(params.styleTags).trim()
    if (styleStr) body.style = styleStr
    if (params && params.make_instrumental != null) {
      body.instrumental = !!params.make_instrumental
    } else if (params && params.instrumental != null) {
      body.instrumental = !!params.instrumental
    }
  } else if (modelName === 'wan-animate') {
    if (params && params.image) body.image = params.image
    if (params && params.video) body.video = params.video
    if (params && params.mode) body.mode = params.mode
    if (params && params.watermark != null) body.watermark = !!params.watermark
  } else {
    body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
    if (params && params.negativePrompt) body.negativePrompt = String(params.negativePrompt).trim()
    if (params && params.referenceImage) body.image = params.referenceImage
    if (params && params.audioUrl) body.audioUrl = params.audioUrl
    if (params && params.videoRes) body.resolution = normalizeResolution(params.videoRes)
    if (params && params.videoMode === 'text' && params.aspectRatio) body.aspectRatio = params.aspectRatio
    if (params && params.promptExtend != null) body.promptExtend = !!params.promptExtend
    if (params && params.videoDuration != null) body.length = params.videoDuration
    if (params && params.seed != null && params.seed !== '') body.seed = params.seed
    if (params && params.watermark != null) body.watermark = !!params.watermark
  }
  return apiClient.post('/aim/call', body).then(function (res) {
    var data = extractData(res)
    // create_id may be returned at response root, while taskId is often nested.
    var createId = extractCreateId(res) || extractCreateId(data)
    var taskId = extractTaskId(res) || extractTaskId(data)
    if (!taskId && !createId) return Promise.reject(new Error('接口未返回 taskId/create_id'))
    return {
      taskId: taskId || createId,
      createId: createId,
      provider: 'wan-unified',
      wanModelName: modelName,
      wanModelVersion: modelVersion,
      wanModelType: modelType
    }
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err && err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

/** z-image / qwen / seedream / midjourney / gpt-image / flux：服务端 /aim/call create → query_state（勿落入 web/v1/generate/image） */
var UNIFIED_DIRECT_IMAGE_MODEL_IDS = [
  'z-image-turbo',
  'qwen-image-2-0-pro',
  'seedream-4',
  'seedream-5',
  'midjourney',
  'gpt-image-1',
  'gpt-image-1-5',
  'flux-pro-1-1-ultra',
  'flux-schnell',
  'flux-2',
  'sd-3-5'
]

function pickMidjourneyReferenceImage(params) {
  var arr = params && params.referenceImagesBase64
  if (!Array.isArray(arr)) return ''
  for (var i = 0; i < arr.length; i++) {
    var u = arr[i]
    if (u != null && String(u).trim() !== '') return String(u).trim()
  }
  return ''
}

/** UI variant（klein-04 等）→ 服务端 model_version */
function flux2UiVariantToServerVersion(variant) {
  var v = String(variant || '').trim()
  if (v === 'klein-04') return '2-klein-4b'
  if (v === 'klein-09') return '2-klein-9b-preview'
  if (v === 'max') return '2-max'
  if (v === 'pro') return '2-pro'
  if (v === 'flex') return '2-flex'
  return '2-pro'
}

function resolveUnifiedDirectImageWanProfile(params) {
  var modelId = params && params.modelId
  if (modelId === 'z-image-turbo') {
    return { modelName: 'z-image', modelVersion: 'turbo' }
  }
  if (modelId === 'qwen-image-2-0-pro') {
    return { modelName: 'qwen-image', modelVersion: '2-0-pro' }
  }
  if (modelId === 'seedream-5') {
    return { modelName: 'seedream', modelVersion: '5' }
  }
  if (modelId === 'seedream-4') {
    return { modelName: 'seedream', modelVersion: '4' }
  }
  if (modelId === 'midjourney') {
    return { modelName: 'midjourney', modelVersion: '1' }
  }
  if (modelId === 'gpt-image-1') {
    return { modelName: 'gpt-image', modelVersion: '1' }
  }
  if (modelId === 'gpt-image-1-5') {
    return { modelName: 'gpt-image', modelVersion: '1-5' }
  }
  if (modelId === 'flux-pro-1-1-ultra') {
    return { modelName: 'flux', modelVersion: 'pro-1-1-ultra' }
  }
  if (modelId === 'flux-schnell') {
    return { modelName: 'flux', modelVersion: 'schnell' }
  }
  if (modelId === 'flux-2') {
    return { modelName: 'flux', modelVersion: flux2UiVariantToServerVersion(params && params.variant) }
  }
  if (modelId === 'sd-3-5') {
    return { modelName: 'sd', modelVersion: '3-5' }
  }
  return null
}

function applyUnifiedDirectImageCreateBody(body, params) {
  var modelId = params && params.modelId
  body.prompt = params && params.prompt ? String(params.prompt).trim() : ''
  if (modelId === 'flux-pro-1-1-ultra') {
    body.aspectRatio = params && params.aspectRatio ? String(params.aspectRatio).trim() : ''
    return
  }
  if (modelId === 'flux-schnell') {
    body.aspectRatio = params && params.aspectRatio ? String(params.aspectRatio).trim() : ''
    return
  }
  if (modelId === 'flux-2') {
    var sd = params && params.seed != null && params.seed !== '' ? Number(params.seed) : 0
    body.seed = isNaN(sd) ? 0 : sd
    body.outputFormat = 'png'
    var resFlux = ''
    if (params && params.resolution != null && String(params.resolution).trim() !== '') {
      resFlux = String(params.resolution).trim()
    } else if (params && params.resolutionPreset && params.resolutionPreset !== 'custom') {
      resFlux = String(params.resolutionPreset).trim()
    }
    body.resolution = resFlux
    body.promptExtend = params && params.promptExtend != null ? !!params.promptExtend : true
    return
  }
  if (modelId === 'z-image-turbo') {
    if (params && params.promptExtend != null) body.promptExtend = !!params.promptExtend
    if (params && params.seed != null && params.seed !== '') body.seed = params.seed
    if (params && params.resolution) body.resolution = String(params.resolution)
    return
  }
  if (modelId === 'sd-3-5') {
    if (params && params.negativePrompt) body.negativePrompt = String(params.negativePrompt).trim()
    if (params && params.resolution) body.resolution = String(params.resolution)
    return
  }
  if (modelId === 'qwen-image-2-0-pro') {
    if (params && params.negativePrompt) body.negativePrompt = String(params.negativePrompt).trim()
    if (params && params.watermark != null) body.watermark = !!params.watermark
    if (params && params.promptExtend != null) body.promptExtend = !!params.promptExtend
    if (params && params.n != null && params.n !== '') body.n = params.n
    if (params && params.resolution) body.resolution = String(params.resolution)
    return
  }
  if (modelId === 'midjourney') {
    body.botType = normalizeBotType(params && (params.style || params.botType || params.mjBotType))
    body.image = pickMidjourneyReferenceImage(params)
    return
  }
  if (modelId === 'gpt-image-1' || modelId === 'gpt-image-1-5') {
    /* 与后端约定：resolution 选分辨率；n 固定为字符串 "1"；仅 1-5 支持 quality */
    if (params && params.resolution != null && String(params.resolution).trim() !== '') {
      body.resolution = String(params.resolution).trim()
    } else if (params && params.imageSize != null && String(params.imageSize).trim() !== '') {
      body.resolution = String(params.imageSize).trim()
    } else {
      body.resolution = ''
    }
    body.n = '1'
    if (modelId === 'gpt-image-1-5') {
      var q = params && params.quality != null ? String(params.quality).trim().toLowerCase() : ''
      if (q === 'low' || q === 'medium' || q === 'high' || q === 'auto') {
        body.quality = q
      } else {
        body.quality = 'auto'
      }
    }
    return
  }
  if (modelId === 'seedream-4' || modelId === 'seedream-5') {
    body.responseFormat = 'url'
    if (params && params.resolution) body.resolution = String(params.resolution).replace(/\*/g, 'x')
    if (params && params.addWatermark != null) body.watermark = !!params.addWatermark
  }
}

function withDefaultModelId(params, modelId) {
  if (params && params.modelId) return params
  var base = params && typeof params === 'object' ? params : {}
  var o = {}
  for (var k in base) {
    if (Object.prototype.hasOwnProperty.call(base, k)) o[k] = base[k]
  }
  o.modelId = modelId
  return o
}

/**
 * 直连图模型统一提交：/aim/call model_action=create，轮询同 pollWanUnifiedStatus + query_state
 */
export function submitUnifiedDirectImageTask(params) {
  var wan = resolveUnifiedDirectImageWanProfile(params)
  if (!wan) {
    return Promise.reject(new Error('不支持的直连图模型'))
  }
  var body = buildBasePayload(wan.modelName, wan.modelVersion, 'image', 'create')
  applyUnifiedDirectImageCreateBody(body, params)
  return apiClient.post('/aim/call', body).then(function (res) {
    var data = extractData(res)
    var createId = extractCreateId(res) || extractCreateId(data)
    var taskId = extractTaskId(res) || extractTaskId(data)
    if (!taskId && !createId) return Promise.reject(new Error('接口未返回 taskId/create_id'))
    var wanPuCreate = wan.modelName === 'flux' ? extractPollingUrlFromPollResponse(res) : ''
    /* create 常见结构：{ data: { code, data: { taskId, pollingUrl, images } } } */
    if (!wanPuCreate && wan.modelName === 'flux' && data && data.data && typeof data.data === 'object') {
      wanPuCreate = pickFirstNonEmpty(data.data, ['pollingUrl', 'polling_url'])
    }
    var out = {
      taskId: taskId || createId,
      createId: createId,
      provider: 'wan-unified',
      wanModelName: wan.modelName,
      wanModelVersion: wan.modelVersion,
      wanModelType: 'image'
    }
    if (wan.modelName === 'flux' && wanPuCreate) out.wanPollingUrl = wanPuCreate
    return out
  }).catch(function (err) {
    var msg = extractApiErrorMessage(err && err.response && err.response.data) || err.message || '请求失败'
    return Promise.reject(new Error(msg))
  })
}

export function submitZImageTurboUnifiedTask(params) {
  return submitUnifiedDirectImageTask(withDefaultModelId(params, 'z-image-turbo'))
}

export function submitQwenImageUnifiedTask(params) {
  return submitUnifiedDirectImageTask(withDefaultModelId(params, 'qwen-image-2-0-pro'))
}

export function submitSeedreamUnifiedTask(params) {
  return submitUnifiedDirectImageTask(withDefaultModelId(params, 'seedream-4'))
}

function queryWanState(taskId, createId, modelName, modelVersion, modelType, pollingUrl) {
  var body = buildBasePayload(modelName, modelVersion, modelType, 'query_state')
  body.create_id = createId
  if (taskId) body.taskId = taskId
  var pUrl = String(pollingUrl || '').trim()
  if (pUrl && modelName === 'flux') body.pollingUrl = pUrl
  var reqCfg = {}
  /* z-image / qwen-image / seedream 等直连图：接口用 code 220 表示生成中；视频等其它模型不走 220 */
  if (normalizeModelType(modelType) === 'image') {
    reqCfg.allowBizCodes = [220]
  }
  return apiClient.post('/aim/call', body, reqCfg)
}

/** Sora / Hailuo 等：query_state 已成功但无 url 时拉取成片；Hailuo 可传 fileId */
function postDownloadVideo(taskId, createId, modelName, modelVersion, modelType, fileId) {
  var body = buildBasePayload(modelName, modelVersion, modelType, 'download_video')
  body.create_id = createId
  if (taskId) body.taskId = taskId
  var fid = String(fileId || '').trim()
  if (fid) body.fileId = fid
  return apiClient.post('/aim/call', body)
}

export function pollWanUnifiedStatus(taskId, callback, interval, options) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var modelName = (options && options.wanModelName) || 'wan'
  var modelVersion = (options && options.wanModelVersion) || '2-5'
  var modelType = normalizeModelType(options && options.wanModelType)
  var createId = (options && options.wanCreateId) || ''
  var isFluxImagePoll = modelName === 'flux' && normalizeModelType(modelType) === 'image'
  var wanPollingUrl = ''
  if (isFluxImagePoll && options && options.wanPollingUrl) {
    wanPollingUrl = String(options.wanPollingUrl).trim()
  }

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function scheduleNext() {
    if (stopped) return
    if (Date.now() > deadline) {
      if (typeof callback === 'function') callback(new Error('轮询超时'), null)
      stop()
      return
    }
    pollTimer = setTimeout(poll, pollInterval)
  }

  function finishWithParsed(parsed) {
    if (typeof callback === 'function') callback(null, parsed)
    stop()
  }

  function poll() {
    if (stopped) return
    queryWanState(taskId, createId, modelName, modelVersion, modelType, wanPollingUrl).then(function (stateRes) {
      if (stopped) return
      /* 接口约定：生成中为 code 220 task pending，接着轮询（stateRes 为拦截器解包后的 body） */
      var p = stateRes
      if (p && typeof p === 'object' && String(p['code'] || '') === '220') {
        if (!createId && p['create_id']) {
          createId = String(p['create_id'])
        }
        if (isFluxImagePoll) {
          var polling220 = extractPollingUrlFromPollResponse(p)
          if (polling220) {
            wanPollingUrl = polling220
            if (options && typeof options === 'object') options.wanPollingUrl = polling220
          }
        }
        if (typeof callback === 'function') callback(null, { status: 'processing' })
        scheduleNext()
        return
      }
      var stateData = extractData(stateRes)
      if (isFluxImagePoll) {
        var pollingFromResp = extractPollingUrlFromPollResponse(stateRes)
        if (pollingFromResp) {
          wanPollingUrl = pollingFromResp
          if (options && typeof options === 'object') options.wanPollingUrl = pollingFromResp
        }
      }

      var modelTypeNorm = normalizeModelType(modelType)

      if (modelTypeNorm === 'image') {
        runWanUnifiedPollImageBranch({
          stateData: stateData,
          stateRes: stateRes,
          modelName: modelName,
          modelType: modelType,
          finishWithParsed: finishWithParsed,
          scheduleNext: scheduleNext,
          callback: callback
        })
        return
      }

      if (modelTypeNorm === 'video') {
        runWanUnifiedPollVideoBranch({
          stateData: stateData,
          modelName: modelName,
          modelType: modelType,
          finishWithParsed: finishWithParsed,
          scheduleNext: scheduleNext,
          callback: callback,
          postDownloadVideo: function (fileIdOpt) {
            return postDownloadVideo(taskId, createId, modelName, modelVersion, modelType, fileIdOpt)
          },
          stopPoll: stop
        })
        return
      }

      if (modelTypeNorm === 'music') {
        runWanUnifiedPollMusicBranch({
          stateData: stateData,
          modelType: modelType,
          finishWithParsed: finishWithParsed,
          scheduleNext: scheduleNext,
          callback: callback
        })
        return
      }

      finishWithParsed({ status: 'failed', error: '不支持的 model_type' })
    }).catch(function (err) {
      if (stopped) return
      if (typeof callback === 'function') callback(err, null)
      stop()
    })
  }

  poll()
  return { stop: stop }
}

export { UNIFIED_DIRECT_IMAGE_MODEL_IDS }
